import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Drawer,
  DrawerTrigger,
  DrawerContent,
  Flex,
  LoadingOrbit,
  Text,
  TagBadge,
} from '@chatsdet/ui';
import { ArrowDown } from '@phosphor-icons/react';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';
import { useRunbookRunStream } from '../../../features/runbooks/useRunbookRunStream';
import {
  formatDateTime,
  formatDuration,
  runbookRunStatusColors,
  runbookRunStatusLabels,
  shortSha,
  type RunbookRun,
} from '../../../features/runbooks/types';

const TERMINAL_STATUSES = new Set(['succeeded', 'failed', 'cancelled', 'timed_out']);

interface RunbookRunDetailDrawerProps {
  runId: string | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Detail view for one runbook run.
 *
 * While the run is queued/running the log streams live over SSE
 * (`useRunbookRunStream`). Once the run reaches a terminal status the drawer
 * switches to the flushed log fetched from `logUrl` — that copy is
 * authoritative and, unlike the stream, survives a page reload.
 */
export function RunbookRunDetailDrawer({ runId, onOpenChange }: RunbookRunDetailDrawerProps) {
  const runQuery = useQuery({
    queryKey: ['runbook-run', runId],
    queryFn: async (): Promise<{ ok: boolean; run: RunbookRun; logUrl: string | null }> => {
      const res = await fetch(`${API_URL}/org/runbook-runs/${runId}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to load run');
      return res.json();
    },
    enabled: !!runId,
    // Terminal runs never change; poll only while it might still be in flight.
    refetchInterval: (query) => {
      const status = query.state.data?.run.status;
      return status && !TERMINAL_STATUSES.has(status) ? 3000 : false;
    },
  });

  const run = runQuery.data?.run ?? null;
  const isTerminal = run ? TERMINAL_STATUSES.has(run.status) : false;

  const stream = useRunbookRunStream(runId && !isTerminal ? runId : null);

  const logQuery = useQuery({
    queryKey: ['runbook-run-log', runId, runQuery.data?.logUrl],
    queryFn: async (): Promise<string> => {
      const url = runQuery.data?.logUrl;
      if (!url) return '';
      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed to fetch run log');
      return res.text();
    },
    enabled: isTerminal && !!runQuery.data?.logUrl,
  });

  const displayStatus = isTerminal ? run?.status : (stream.status ?? run?.status);

  /**
   * The flushed log wins once it has actually arrived; until then, whatever was
   * streamed stands.
   *
   * Not `isTerminal ? logQuery.data ?? '' : stream.log`. That empties the pane
   * for the frame between the run going terminal and the fetch resolving — and
   * worse, leaves it empty forever when `logStorageKey` is null, which
   * `run.job.ts` treats as an acceptable outcome (the S3 flush is best-effort
   * enrichment, deliberately not allowed to fail a finished deploy). Throwing
   * away output we already have, to show "no log recorded", is the wrong answer
   * in exactly the case someone is trying to find out what happened.
   */
  const log = logQuery.data ?? stream.log;

  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = React.useState(true);

  React.useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [log, autoScroll]);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    setAutoScroll(scrollHeight - scrollTop - clientHeight < 50);
  };

  return (
    <DrawerTrigger
      isOpen={!!runId}
      onOpenChange={(open) => {
        if (!open) onOpenChange(false);
      }}
    >
      {/* Hidden trigger button — drawer is controlled externally */}
      <Button className="hidden" />

      <Drawer title={run ? run.slug : 'Run'}>
        <DrawerContent className="p-0 flex flex-col h-full">
          {runQuery.isLoading ? (
            <Flex align="center" justify="center" className="py-8">
              <LoadingOrbit size="md" />
            </Flex>
          ) : runQuery.isError ? (
            <Text size="sm" className="text-destructive p-4">
              Couldn&apos;t load this run.
            </Text>
          ) : run ? (
            <>
              <div className="px-4 py-3 border-b border-border bg-muted/30 space-y-2 shrink-0">
                <Flex align="center" gap="sm" wrap>
                  {displayStatus && (
                    <TagBadge
                      name={runbookRunStatusLabels[displayStatus]}
                      color={runbookRunStatusColors[displayStatus]}
                      hideDot
                    />
                  )}
                  <Text size="xs" className="text-muted-foreground font-mono">
                    {shortSha(run.resolvedSha)}
                  </Text>
                  {run.exitCode !== null && (
                    <Text size="xs" className="text-muted-foreground">
                      Exit code {run.exitCode}
                    </Text>
                  )}
                  <Text size="xs" className="text-muted-foreground">
                    {formatDuration(run.startedAt, run.finishedAt)}
                  </Text>
                </Flex>
                <Text size="xs" className="text-muted-foreground">
                  Started {formatDateTime(run.startedAt)}
                </Text>
                {/* The streamed reason arrives before the row is refetched, and
                    is the only thing a watcher gets when a run fails before it
                    produced any output — there is no log to fall back on. */}
                {(run.error ?? stream.error) && (
                  <Flex direction="column" gap="xs" className="rounded-md bg-destructive/10 p-2">
                    <Text size="xs" weight="medium" className="text-destructive">
                      Run failed to execute
                    </Text>
                    <Text size="xs" className="text-destructive">
                      {run.error ?? stream.error}
                    </Text>
                  </Flex>
                )}
              </div>

              <div className="flex items-center justify-between px-4 py-2 bg-muted border-b border-border shrink-0">
                <Text size="xs" weight="medium" className="text-muted-foreground uppercase tracking-wide">
                  Log
                </Text>
                {!autoScroll && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onPress={() => {
                      setAutoScroll(true);
                      if (scrollRef.current) {
                        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
                      }
                    }}
                    aria-label="Scroll to bottom"
                  >
                    <ArrowDown size={16} />
                  </Button>
                )}
              </div>

              <Box
                ref={scrollRef}
                onScroll={handleScroll}
                className="flex-1 overflow-auto bg-muted py-2"
              >
                {log.length === 0 ? (
                  <Text size="sm" className="text-muted-foreground text-center py-8">
                    {isTerminal
                      ? logQuery.isLoading
                        ? 'Loading log…'
                        : 'No log recorded for this run.'
                      : 'Waiting for output…'}
                  </Text>
                ) : (
                  <>
                    {stream.hasGap && logQuery.data === undefined ? (
                      <Text size="sm" className="text-muted-foreground px-4 pb-2">
                        Part of this run&rsquo;s output was missed while disconnected. The complete
                        log is available once the run finishes.
                      </Text>
                    ) : null}
                    <pre className="font-mono text-xs px-4 whitespace-pre-wrap break-all">{log}</pre>
                  </>
                )}
              </Box>
            </>
          ) : null}
        </DrawerContent>
      </Drawer>
    </DrawerTrigger>
  );
}
